import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Siren, AlertTriangle, Info, CalendarClock, Video, MapPin, ChevronRight, RefreshCw, Hourglass, CheckCircle2, CheckCheck, PenLine, XCircle, PhoneMissed, UserX, UserRound, Stethoscope, History, FileText, ClipboardEdit } from "lucide-react";
import DashboardLayout from "../../layouts/DashboardLayout";
import useFeatures from "../../hooks/useFeatures";
import api from "../../api";
import DueFollowUpsCard from "../../components/dashboard/DueFollowUpsCard";

// Clinician home (backend clinical/workspace_routes.py): today's sessions,
// what needs attention, and caseload counts. Doctors land here from Home
// while the "today" feature is on.
const SEV = {
  urgent: { icon: Siren, box: "border-rose-300 bg-rose-50", text: "text-rose-800" },
  important: { icon: AlertTriangle, box: "border-amber-200 bg-amber-50", text: "text-amber-900" },
  info: { icon: Info, box: "border-secondary-200 bg-white", text: "text-secondary-800" },
};
// Patient goal check-in answers (treatment plan), same wording as the patient app.
const CHECKIN = { not_yet: ["Not yet", "bg-secondary-100 text-secondary-700"], some_days: ["Some days", "bg-amber-100 text-amber-800"], most_days: ["Most days", "bg-emerald-50 text-emerald-700"], done: ["Done it", "bg-emerald-600 text-white"] };
const RISK = { low: "bg-emerald-50 text-emerald-700", moderate: "bg-amber-100 text-amber-800", high: "bg-rose-100 text-rose-700" };

const fmtShort = (iso, withYear = false) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}) }) : "");
const daysAgo = (iso) => {
  if (!iso) return "";
  const n = Math.round((new Date(new Date().toDateString()) - new Date(`${iso}T00:00:00`)) / 86400000);
  return n <= 0 ? "" : n === 1 ? " (yesterday)" : ` (${n} days ago)`;
};

// Where a session stands right now: ongoing (notes started, or inside its
// time slot), upcoming, or finished (seen, missed, declined or cancelled).
const toMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + (m || 0); };
function phase(s, nowMin) {
  const cancelled = s.is_active === false || /cancel|declin/i.test(s.confirmation_status || "");
  if (s.consultation_status === "completed" || cancelled) return "done";
  if (s.attendance === "no_show") return "no_show";
  if (s.consultation_id) return "ongoing";
  const start = toMin(s.time), end = start + (s.duration_minutes || 30);
  if (nowMin >= start && nowMin < end) return "ongoing";
  if (nowMin >= end) return "missed";
  return "upcoming";
}
const PHASE_ORDER = { ongoing: 0, upcoming: 1, missed: 2, no_show: 2, done: 3 };

export function TodayQueueContent() {
  const navigate = useNavigate();
  const features = useFeatures();
  const [data, setData] = useState(null);
  const [showAllAttention, setShowAllAttention] = useState(false);
  const ATTENTION_LIMIT = 4; // keep Follow-ups in view; the rest one click away
  const [error, setError] = useState("");
  const [who, setWho] = useState("all"); // team / clinic view: filter by clinician

  const load = () => api.getToday().then((r) => { setData(r.data); setError(""); }).catch((e) => setError(e?.response?.data?.error || "Couldn't load today's work."));
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  if (error) return <div className="p-8 text-red-600">{error}</div>;
  if (!data) return <div className="p-8 text-secondary-500">Loading…</div>;

  const hello = new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 17 ? "Good afternoon" : "Good evening";
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const clinicians = [...new Set(data.sessions.map((s) => (s.is_mine ? "You" : s.doctor_name)).filter(Boolean))];
  const sessions = [...data.sessions]
    .filter((s) => who === "all" || (s.is_mine ? "You" : s.doctor_name) === who)
    .map((s) => ({ ...s, _phase: phase(s, nowMin) }))
    .sort((a, b) => PHASE_ORDER[a._phase] - PHASE_ORDER[b._phase] || (a.time || "").localeCompare(b.time || ""));
  const firstFinished = sessions.findIndex((s) => ["missed", "no_show", "done"].includes(s._phase));
  const markNoShow = async (s) => {
    if (!window.confirm(`Record that ${s.patient_name} didn't attend? They'll get a message inviting them to rebook.`)) return;
    try { await api.setAppointmentAttendance(s.appointment_id, "no_show"); load(); }
    catch (e) { alert(e?.response?.data?.error || "Couldn't record that."); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-sm text-secondary-600"><Sun className="w-4 h-4" /> {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</p>
          <h1 className="text-2xl font-bold text-secondary-900">{hello}</h1>
        </div>
        <button type="button" onClick={load} className="inline-flex items-center gap-1.5 text-sm text-secondary-500 hover:text-primary-600"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Sessions today", data.counts.sessions_today], ["This week", data.counts.sessions_this_week], ["Caseload", data.counts.caseload], ["Needs attention", data.counts.needs_attention]].map(([label, n]) => (
          <div key={label} className="rounded-xl bg-white border border-secondary-100 p-4">
            <p className="text-xs text-secondary-600 font-medium">{label}</p>
            <p className={`text-2xl font-bold ${label === "Needs attention" && n > 0 ? "text-rose-600" : "text-secondary-900"}`}>{n}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <section className="lg:col-span-3 space-y-3">
          <div>
            <h2 className="flex items-center gap-2 text-base font-bold text-secondary-900"><CalendarClock className="w-5 h-5 text-primary-600" /> Today's sessions</h2>
            <p className="text-xs text-secondary-600">{data.scope === "clinic" ? "Everyone in your clinic" : data.scope === "team" ? "You and your team" : "Booked with you"}</p>
          </div>
          {data.scope !== "mine" && clinicians.length > 1 && (
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter sessions by clinician">
              {["all", ...clinicians].map((c) => {
                const n = c === "all" ? data.sessions.length : data.sessions.filter((s) => (s.is_mine ? "You" : s.doctor_name) === c).length;
                return (
                  <button key={c} type="button" onClick={() => setWho(c)} aria-pressed={who === c}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold ${who === c ? "bg-primary-600 border-primary-600 text-white" : "bg-white border-secondary-200 text-secondary-700 hover:bg-secondary-50"}`}>
                    {c === "all" ? "Everyone" : c} <span className={who === c ? "text-white/80" : "text-secondary-500"}>{n}</span>
                  </button>
                );
              })}
            </div>
          )}
          {data.sessions.length === 0 ? (
            <p className="rounded-xl bg-white border border-secondary-100 p-6 text-sm text-secondary-500">{data.scope === "clinic" ? "No sessions in your clinic today." : "No sessions booked with you today."}</p>
          ) : (
            <ul className="space-y-3">
              {sessions.map((s, idx) => {
                const done = s.consultation_status === "completed";
                const ongoing = s._phase === "ongoing";
                const finished = ["missed", "no_show", "done"].includes(s._phase);
                return (
                  <Fragment key={s.appointment_id}>
                  {idx === firstFinished && idx > 0 && (
                    <li aria-hidden="true" className="flex items-center gap-3 pt-2 text-[11px] font-bold uppercase tracking-wider text-secondary-400">
                      <span>Done &amp; missed</span><span className="h-px flex-1 bg-secondary-200" />
                    </li>
                  )}
                  <li className={`relative overflow-hidden flex flex-wrap sm:flex-nowrap items-center gap-4 p-4 rounded-xl border shadow-sm ${ongoing ? "bg-emerald-50 border-emerald-200" : finished ? "bg-secondary-50 border-secondary-200" : "bg-white border-secondary-200"}`}>
                    {ongoing && <span className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500" aria-hidden="true" />}
                    {/* Time and visit type together */}
                    <div className={`w-20 shrink-0 self-center rounded-lg px-2 py-2 text-center ${ongoing ? "bg-emerald-600 text-white" : finished ? "bg-secondary-200 text-secondary-700" : "bg-secondary-100 text-secondary-800"}`}>
                      <p className="text-base font-bold leading-tight">{s.time}</p>
                      <p className="flex items-center justify-center gap-1 text-[11px] leading-tight mt-0.5">
                        {(s.appttype || "").toLowerCase() === "online"
                          ? <><Video className="w-3 h-3" aria-hidden="true" /> {s.audio_only ? "Voice" : "Video"}</>
                          : <><MapPin className="w-3 h-3" aria-hidden="true" /> Clinic</>}
                      </p>
                    </div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span aria-hidden="true" className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${ongoing ? "bg-emerald-600 text-white" : "bg-secondary-200 text-secondary-700"}`}>{(s.patient_name || "?").trim().charAt(0).toUpperCase()}</span>
                        <p title={s.patient_name} className="text-base font-bold text-secondary-900 break-words min-w-0">{s.patient_name}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {(() => {
                          const pill = ongoing ? null
                            : s._phase === "no_show" ? ["No-show", "bg-rose-100 text-rose-700", UserX]
                            : s._phase === "missed" ? ["Missed", "bg-rose-100 text-rose-700", PhoneMissed]
                            : done ? ["Completed", "bg-emerald-100 text-emerald-800", CheckCheck]
                            : s.consultation_id ? ["Notes started", "bg-primary-50 text-primary-700", PenLine]
                            : s.confirmation_status === "Pending Patient Acceptance" ? ["Waiting for patient", "bg-amber-100 text-amber-800", Hourglass]
                            : (s.confirmation_status || "").toLowerCase().includes("cancel") ? ["Cancelled", "bg-rose-100 text-rose-700", XCircle]
                            : ["Confirmed", "bg-emerald-50 text-emerald-700", CheckCircle2];
                          const PillIcon = pill && pill[2];
                          return pill ? (
                            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${pill[1]}`}><PillIcon className="w-3.5 h-3.5" aria-hidden="true" />{pill[0]}</span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white px-2.5 py-0.5 text-xs font-semibold">
                              <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" /></span>
                              In progress
                            </span>
                          );
                        })()}
                        {s.risk_level && RISK[s.risk_level] && <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${RISK[s.risk_level]}`}>{s.risk_level[0].toUpperCase() + s.risk_level.slice(1)} risk</span>}
                        {(s.appttype || "").toLowerCase() === "online" && s.payment_mode === "Online" && s.payment_status !== "Prepaid" && !finished && (
                          <span title="Online visit not paid yet - the patient can't join until it's paid" className="rounded-full bg-amber-100 text-amber-800 px-2.5 py-0.5 text-xs font-semibold cursor-help">Not paid</span>
                        )}
                        {s.relationship === "new_patient" && <span title="First time at your clinic" className="rounded-full bg-sky-100 text-sky-800 px-2.5 py-0.5 text-xs font-semibold">New patient</span>}
                        {s.relationship === "new_to_you" && <span title={`Seen at your clinic since ${fmtShort(s.in_clinic_since, true)} - their earlier notes are in the patient file.`} className="rounded-full bg-violet-100 text-violet-800 px-2.5 py-0.5 text-xs font-semibold cursor-help">New to {s.is_mine ? "you" : "clinician"}</span>}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-secondary-600">
                        {!s.is_mine && s.doctor_name && <span className="inline-flex items-center gap-1.5 font-semibold text-primary-700"><Stethoscope className="w-4 h-4" aria-hidden="true" />{s.doctor_name}</span>}
                        {(s.relationship === "returning" || s.planned_sessions) && <span title={s.planned_sessions ? `${s.planned_sessions} sessions planned in the treatment plan` : undefined} className="inline-flex items-center gap-1.5"><CalendarClock className="w-4 h-4 text-emerald-600" aria-hidden="true" />Session {s.session_number}{s.planned_sessions ? ` of ${s.planned_sessions}` : ""}</span>}
                        {(s.relationship === "returning" || s.relationship === "new_to_you") && (s.first_session_with_doctor || s.in_clinic_since) && (
                          <span className="inline-flex items-center gap-1.5"><UserRound className="w-4 h-4 text-emerald-600" aria-hidden="true" />{s.relationship === "returning" ? `${s.is_mine ? "With you" : "With them"} since ${fmtShort(s.first_session_with_doctor, true)}` : `In care since ${fmtShort(s.in_clinic_since, true)}`}</span>
                        )}
                        {s.relationship === "returning" && <span className="inline-flex items-center gap-1.5"><History className="w-4 h-4 text-emerald-600" aria-hidden="true" />Last seen {fmtShort(s.last_session_with_doctor)}{daysAgo(s.last_session_with_doctor)}</span>}
                        {s.no_shows > 0 && <span title="Recorded no-shows with this clinic" className="inline-flex items-center gap-1.5 font-semibold text-amber-700"><AlertTriangle className="w-4 h-4" aria-hidden="true" />{s.no_shows} no-show{s.no_shows === 1 ? "" : "s"}</span>}
                        {(s.appttype || "").toLowerCase() !== "online" && s.branch_name && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-emerald-600" aria-hidden="true" />{s.branch_name}</span>}
                      </div>
                      {s.plan_progress && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-0.5">
                          <button type="button" onClick={() => navigate(`/patient/${s.patient_id}?tab=plan`)} title="Open the treatment plan" className="group inline-flex items-center gap-3 rounded-md -mx-1 px-1 hover:bg-secondary-50">
                            <span className="h-2 w-40 max-w-[40vw] overflow-hidden rounded-full bg-secondary-200" role="progressbar" aria-label="Treatment plan goals reached" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(100 * s.plan_progress.met / s.plan_progress.total)}>
                              <span className="block h-full rounded-full bg-emerald-600" style={{ width: `${Math.round(100 * s.plan_progress.met / s.plan_progress.total)}%` }} />
                            </span>
                            <span className="text-sm text-secondary-700 group-hover:text-primary-700 group-hover:underline">Treatment plan: {s.plan_progress.met} of {s.plan_progress.total} goal{s.plan_progress.total === 1 ? "" : "s"} reached</span>
                          </button>
                          {s.plan_progress.latest_checkin && (() => {
                            const c = s.plan_progress.latest_checkin;
                            const [label, tone] = CHECKIN[c.rating] || [c.rating, "bg-secondary-100 text-secondary-700"];
                            return (
                              <span title={`Latest goal check-in from the patient, on "${c.goal}"${c.note ? `: ${c.note}` : ""}`} className="inline-flex items-center gap-1.5 text-sm text-secondary-600">
                                Patient says <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>{label}</span><span className="text-secondary-500">{new Date(c.at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                              </span>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0 w-full sm:w-44 sm:ml-auto">
                      {s._phase === "missed" && features.has("attendance") && (
                        <button type="button" onClick={() => markNoShow(s)} className="rounded-lg border border-rose-300 text-rose-700 text-sm font-semibold px-3 py-2 hover:bg-rose-50">Mark no-show</button>
                      )}
                      <button type="button" onClick={() => navigate(`/patient/${s.patient_id}`)} className="inline-flex items-center justify-center gap-2 rounded-lg border border-secondary-300 bg-white text-sm font-semibold px-3 py-2 text-secondary-800 hover:bg-secondary-50"><FileText className="w-4 h-4" aria-hidden="true" />Patient file</button>
                      {s.consultation_id ? (
                        <button type="button" onClick={() => navigate(`/consultation/${s.consultation_id}`)} className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold px-3 py-2"><ClipboardEdit className="w-4 h-4" aria-hidden="true" />{done ? "Session notes" : "Continue notes"}</button>
                      ) : (
                        <button type="button" onClick={() => navigate(`/appointment?highlight=${s.appointment_id}`)} title="Open this appointment on the Appointments page" className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold px-3 py-2"><CalendarClock className="w-4 h-4" aria-hidden="true" />Open</button>
                      )}
                    </div>
                  </li>
                  </Fragment>
                );
              })}
            </ul>
          )}
        </section>

        <section className="lg:col-span-2 space-y-3">
          <h2 className="flex items-center gap-2 text-base font-bold text-secondary-900"><AlertTriangle className="w-5 h-5 text-amber-500" /> Needs your attention</h2>
          {data.attention.length === 0 ? (
            <p className="rounded-xl bg-white border border-secondary-100 p-6 text-sm text-secondary-500">Nothing needs you right now.</p>
          ) : (
            <>
            <ul className="space-y-2">
              {(showAllAttention ? data.attention : data.attention.slice(0, ATTENTION_LIMIT)).map((a, i) => {
                const s = SEV[a.severity] || SEV.info;
                const Icon = s.icon;
                return (
                  <li key={i}>
                    <button type="button" onClick={() => navigate(a.link)} className={`w-full text-left flex items-start gap-3 rounded-xl border p-3 hover:shadow-sm ${s.box}`}>
                      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${s.text}`} />
                      <span className="flex-1 min-w-0">
                        <span className={`block text-sm font-semibold ${s.text}`}>{a.title}</span>
                        {a.detail && <span className="block text-xs text-secondary-600">{a.detail}</span>}
                      </span>
                      <ChevronRight className="w-4 h-4 mt-0.5 text-secondary-400" />
                    </button>
                  </li>
                );
              })}
            </ul>
            {data.attention.length > ATTENTION_LIMIT && (
              <button type="button" onClick={() => setShowAllAttention((v) => !v)} className="w-full rounded-lg border border-secondary-200 bg-white py-2 text-xs font-semibold text-primary-700 hover:bg-secondary-50">
                {showAllAttention ? "Show fewer" : `Show ${data.attention.length - ATTENTION_LIMIT} more`}
              </button>
            )}
            </>
          )}
          <DueFollowUpsCard />
        </section>
      </div>
    </div>
  );
}

export default function TodayQueue() {
  const features = useFeatures();
  return (
    <DashboardLayout>
      {features.ready && !features.has("today")
        ? <div className="p-8 text-secondary-600">The Today page isn't switched on for your organisation yet.</div>
        : <TodayQueueContent />}
    </DashboardLayout>
  );
}
